import type { SessionResponse, WorkspaceSummary } from '@schemas/session'
import { type WorkspaceRole, WorkspaceRoleEnum } from '@schemas/workspace'
import { use } from 'react'
import { UNSAFE_DataRouterStateContext } from 'react-router'

// RAL-247: the one place the shell decides whether to render admin-only controls. Members get the
// control removed, never disabled. The API is the real gate (requireWorkspaceAdmin); this only keeps
// a member from seeing a button that would 403. A session with no workspace counts as an admin so a
// surface's full layout stays the default.
// Takes any session object because src/api.ts's hand-written SessionResponse predates the workspace fields.
export const canManage = (session: object | null | undefined): boolean =>
  (session as Partial<SessionResponse> | null | undefined)?.activeWorkspace?.role !== WorkspaceRoleEnum.enum.member

// Reads the root loader's session through the router state context rather than useRouteLoaderData, which
// throws outside a data router. Leaf components (Queue rows, the connect button) render in plenty of
// places without one, and they should fall back to the admin layout there instead of crashing.
// Upgrade risk: UNSAFE_DataRouterStateContext is react-router's internal context, exempt from semver. If a
// major renames it typecheck fails; if it keeps the name but reshapes loaderData, this silently returns admin
// for everyone. The renderAsRole member tests mount a real data router, so they fail on that reshape; run
// them on every react-router bump. The stable replacement is a small context RootLayout provides.
export const useCanManage = (): boolean => canManage(use(UNSAFE_DataRouterStateContext)?.loaderData.root)

// RAL-121 rule 1: a switcher row names the capability, not just the role — "Member" tells a
// creator nothing about what they can do when they land there, and switching can take publishing
// away. Seat capacity is deliberately absent (rule 10): it renders only in Team, in part 2.
export const workspaceCapabilityLine = (workspace: WorkspaceSummary): string => {
  const accounts = `${workspace.accountCount} ${workspace.accountCount === 1 ? 'account' : 'accounts'}`
  return workspace.role === WorkspaceRoleEnum.enum.member ? `${accounts} · analytics only` : accounts
}

// RAL-121 rule 4: admins land on Queue because approving is their work, members on Published
// because reading results is theirs. Used both by the index route and after a workspace switch,
// so a switch that changes your role changes where you come out.
export const landingPathForRole = (role: WorkspaceRole): string =>
  role === WorkspaceRoleEnum.enum.member ? '/published' : '/queue'
